import React, { useEffect, useMemo, useState } from "react";
import { AxiosInstance } from "axios";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import { AdminCarrierBridgeResponse } from "./CarrierBridgeData";
import {
  CarrierBridgeFormErrors,
  CarrierBridgeFormState,
  buildCarrierBridgePayload,
  describeCarrierBridgeError,
  emptyCarrierBridgeForm,
  formFromService,
  portLabel,
  portTenantName,
  validateCarrierBridgeForm,
} from "./carrierBridgeForm";
import { carrierBridgeAdminText } from "./carrierBridgeLabels";
import {
  CarrierOption,
  PortOption,
  createCarrierBridge,
  updateCarrierBridge,
} from "./request/request";

export type CarrierBridgeModalMode = "create" | "edit" | "view";

interface CarrierBridgeModalProps {
  isOpen: boolean;
  mode: CarrierBridgeModalMode;
  /** The service to edit or view; ignored on create. */
  service: AdminCarrierBridgeResponse | null;
  ports: PortOption[];
  carriers: CarrierOption[];
  optionsLoading: boolean;
  api: AxiosInstance;
  onClose: () => void;
  /** Called after a successful save so the list can refresh. */
  onSaved: () => void;
}

const text = carrierBridgeAdminText.modal;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="col-span-2 text-xs font-semibold tracking-wide text-semantic-text-secondary">
    {children}
  </h3>
);

const titleByMode: Record<CarrierBridgeModalMode, string> = {
  create: text.createTitle,
  edit: text.editTitle,
  view: text.viewTitle,
};

/**
 * Create, edit and view one Carrier Bridge (CTX-724), laid out as the approved
 * design: Service, Connectivity and Contract. The customer is never picked: it
 * follows the port, as the backend enforces.
 */
const CarrierBridgeModal: React.FC<CarrierBridgeModalProps> = ({
  isOpen,
  mode,
  service,
  ports,
  carriers,
  optionsLoading,
  api,
  onClose,
  onSaved,
}) => {
  const initialForm = useMemo(
    () => (mode !== "create" && service ? formFromService(service) : emptyCarrierBridgeForm),
    [mode, service]
  );
  const [form, setForm] = useState<CarrierBridgeFormState>(initialForm);
  const [errors, setErrors] = useState<CarrierBridgeFormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm);
    setErrors({});
    setApiError(null);
  }, [isOpen, initialForm]);

  const readOnly = mode === "view";
  const selectedPort = ports.find((p) => String(p.id) === form.portId);

  // The service's own carrier stays listed on edit even after it was hidden
  // from new services; the backend lets a service keep it.
  const carrierItems = useMemo(() => {
    const items = carriers.map((c) => ({ value: String(c.id), label: c.name }));
    const own = service?.carrier;
    if (own && !items.some((i) => i.value === String(own.id))) {
      items.push({ value: String(own.id), label: own.name });
    }
    return items;
  }, [carriers, service]);

  const portItems = useMemo(
    () => ports.map((p) => ({ value: String(p.id), label: portLabel(p) })),
    [ports]
  );

  const update = <K extends keyof CarrierBridgeFormState>(
    key: K,
    value: CarrierBridgeFormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    const found = validateCarrierBridgeForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSaving(true);
    setApiError(null);
    try {
      if (mode === "create") {
        await createCarrierBridge(api, buildCarrierBridgePayload(form));
      } else if (service) {
        const changes = buildCarrierBridgePayload(form, initialForm);
        if (Object.keys(changes).length > 0) {
          await updateCarrierBridge(api, service.id, changes);
        }
      }
      onSaved();
      onClose();
    } catch (error) {
      // Stays open with the backend's reason (VLAN taken, port of another
      // tenant, carrier hidden…) instead of closing on a failed save.
      console.error("Error saving Carrier Bridge", error);
      setApiError(describeCarrierBridgeError(error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <GenericButton
        label={readOnly ? text.close : text.cancel}
        variant="secondary"
        onClick={onClose}
        disabled={isSaving}
      />
      {!readOnly && (
        <GenericButton
          label={text.save}
          variant="brand"
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || optionsLoading}
        />
      )}
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={titleByMode[mode]}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        {apiError && (
          <GenericInlineAlert variant="error">{apiError}</GenericInlineAlert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <SectionTitle>{text.sections.service}</SectionTitle>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-semantic-text-primary">
              {text.customerPort} *
            </span>
            <GenericSearchableDropdown
              items={portItems}
              value={form.portId}
              onSelect={(value) => update("portId", value)}
              defaultLabel={optionsLoading ? text.loadingOptions : text.selectPort}
              loading={optionsLoading}
              disabled={readOnly || optionsLoading}
              size="input"
              widthClass="w-full"
              fixedPosition
            />
            {errors.portId && (
              <span className="text-xs text-semantic-text-danger">{errors.portId}</span>
            )}
          </div>
          <GenericInput
            label={text.customer}
            value={portTenantName(selectedPort)}
            placeholder={text.customerHint}
            readOnly
            fullWidth
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-semantic-text-primary">
              {text.carrier} *
            </span>
            <GenericSearchableDropdown
              items={carrierItems}
              value={form.carrierId}
              onSelect={(value) => update("carrierId", value)}
              defaultLabel={optionsLoading ? text.loadingOptions : text.selectCarrier}
              emptyItemsMessage={text.noCarriers}
              loading={optionsLoading}
              disabled={readOnly || optionsLoading}
              size="input"
              widthClass="w-full"
              fixedPosition
            />
            {errors.carrierId && (
              <span className="text-xs text-semantic-text-danger">{errors.carrierId}</span>
            )}
          </div>
          <GenericInput
            label={text.circuitId}
            value={form.circuitId}
            onChange={(value) => update("circuitId", value)}
            placeholder="e.g. CKT-88213"
            readOnly={readOnly}
            fullWidth
          />

          <SectionTitle>{text.sections.connectivity}</SectionTitle>
          <div className="flex flex-col gap-2">
            <GenericInput
              label={text.vlan}
              value={form.wholePort ? "" : form.vlan}
              onChange={(value) => update("vlan", value)}
              required={!form.wholePort}
              disabled={form.wholePort}
              readOnly={readOnly}
              hasError={Boolean(errors.vlan)}
              helperText={errors.vlan}
              fullWidth
            />
            <GenericCheckbox
              label={text.wholePort}
              checked={form.wholePort}
              onChange={(checked) => update("wholePort", checked)}
              disabled={readOnly}
            />
          </div>
          <GenericInput
            label={text.mbps}
            value={form.mbps}
            onChange={(value) => update("mbps", value)}
            required
            readOnly={readOnly}
            hasError={Boolean(errors.mbps)}
            helperText={errors.mbps}
            fullWidth
          />

          {/* No term or auto-renew: both belong to the subscription, created
              when the service goes live or from Subscriptions (CTX-805). */}
          <SectionTitle>{text.sections.details}</SectionTitle>
          <GenericInput
            label={text.serviceId}
            value={service?.subscriptionId ?? ""}
            placeholder={text.serviceIdPending}
            readOnly
            fullWidth
          />
          <GenericInput
            label={text.name}
            value={form.name}
            onChange={(value) => update("name", value)}
            placeholder={text.namePlaceholder}
            readOnly={readOnly}
            fullWidth
          />
          <div className="flex flex-col justify-end gap-2 pb-1">
            <GenericCheckbox
              label={text.draft}
              checked={form.isDraft}
              onChange={(checked) => update("isDraft", checked)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

export default CarrierBridgeModal;
